import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import { getCalendarByCompanyIdsCentered } from '~/database/supabase/calendar/queries.server.ts';
import {
	getCompaniesAndInstrumentsBasicInfo,
	getMfnCompaniesAndInstrumentsBasicInfo,
	getMfnCompanyIds,
	getUserCompanyIds,
} from '~/database/supabase/instrument_company/queries.server.ts';
import type {
	CalendarEventWithCompanyNamesAndInstrumentBasicInfoType,
	CompanyNamesAndInstrumentBasicInfoType,
} from '~/database/supabase/instrument_company/types.ts';
import { getNewsByCompanyIds } from '~/database/supabase/news/queries.server.ts';
import { getHints } from '~/utils/client-hints.tsx';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

export interface NewsMeta {
	readonly day_change: string | null;
	readonly day_volume: string | null;
}

export interface NewsArticle {
	readonly company_id: number;
	readonly created_at: string;
	readonly day_change: string | null;
	readonly day_volume: string | null;
	readonly description: string;
	readonly href: string;
	readonly id: number;
	readonly lang: string;
	readonly meta: NewsMeta;
	readonly news_report_id: number | null;
	readonly published_at: Date | string | null;
	readonly streamed: boolean;
	readonly time: string;
}

export interface NewsWithCompanies {
	readonly articles?: NewsArticle;
	readonly company?: CompanyNamesAndInstrumentBasicInfoType;
}

export interface CompanyFilterOption {
	readonly id: string;
	readonly name: string;
}

export interface NewsPageData {
	readonly calendar: {
		readonly data: CalendarEventWithCompanyNamesAndInstrumentBasicInfoType[] | undefined;
		readonly page: number;
	};
	readonly companyFilterOptions: CompanyFilterOption[];
	readonly companyIdsMonitored: number[] | undefined;
	readonly news: {
		readonly data: NewsWithCompanies[] | undefined;
		readonly page: number;
	};
	readonly userLocale: string;
}

const GetNewsPageDataSchema = z.object({
	company: z.string().optional(),
	type: z.enum(['all', 'interest']).optional(),
});

export const getNewsPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.user-company-ids',
				'supabase.mfn-company-ids',
				'supabase.news.by-company',
				'supabase.calendar.centered',
				'supabase.company-basics',
			],
			relatesTo: 'news.page',
		}),
	])
	.inputValidator(GetNewsPageDataSchema)
	.handler(async ({ data }): Promise<NewsPageData> => {
		const request = getRequest();
		const authSession = await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;
		const onlyInterested = data.type === 'interest';

		let companyIds: number[] | null = null;
		if (onlyInterested) {
			companyIds = await getUserCompanyIds({ userId: authSession.userId });
		} else {
			companyIds = await getMfnCompanyIds();
		}

		if (!companyIds) {
			return {
				calendar: {
					data: undefined,
					page: 0,
				},
				companyFilterOptions: [],
				companyIdsMonitored: undefined,
				news: {
					data: undefined,
					page: 0,
				},
				userLocale,
			};
		}

		const selectedCompanyId = data.company ? Number.parseInt(data.company, 10) : Number.NaN;
		const hasSelectedCompany = Number.isInteger(selectedCompanyId) && companyIds.includes(selectedCompanyId);
		const newsCompanyIds = hasSelectedCompany ? [selectedCompanyId] : companyIds;

		const [news, calendar, companies] = await Promise.all([
			getNewsByCompanyIds(newsCompanyIds, 0),
			getCalendarByCompanyIdsCentered(companyIds),
			onlyInterested ? getCompaniesAndInstrumentsBasicInfo(companyIds) : getMfnCompaniesAndInstrumentsBasicInfo(),
		]);

		const companyById = new Map((companies ?? []).map((company) => [Number(company.id), company]));

		const companyFilterOptionsMap = new Map<string, CompanyFilterOption>();
		for (const company of companies ?? []) {
			const id = String(company.id);
			const name = company.name ?? '';

			if (!name) {
				continue;
			}

			companyFilterOptionsMap.set(id, {
				id,
				name,
			});
		}

		const companyFilterOptions = Array.from(companyFilterOptionsMap.values()).sort((a, b) =>
			a.name.localeCompare(b.name),
		);

		const formattedNews = news?.map((newsRow) => ({
			articles: {
				...newsRow,
				company_id: newsRow.company_id,
				id: newsRow.id,
				meta: {
					day_change: newsRow.day_change,
					day_volume: newsRow.day_volume,
				},
				news_report_id: newsRow.news_report_id,
				published_at: new Date(`${newsRow.date}T${newsRow.time}`),
			},
			company: companyById.get(Number(newsRow.company_id)),
		}));

		const formattedCalendarEvents = calendar?.events?.map((event) => ({
			company: companyById.get(Number(event.company_id)),
			event,
		}));

		return {
			calendar: {
				data: formattedCalendarEvents,
				page: calendar?.page ?? 0,
			},
			companyFilterOptions,
			companyIdsMonitored: companies?.map((company) => Number(company.id)),
			news: {
				data: formattedNews,
				page: 0,
			},
			userLocale,
		};
	});
